import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isLoggedIn, goToStudyGuides, getTasks, getAssignments, getNotifications, getFeedbackBanks, getClasses } from '../../service';
import jeddaiIconIcon from '../../static/img/JeddAI-Icon-active.svg';
import taskIcon from '../../static/img/Task2.svg';
import activeClassIcon from '../../static/img/active-class-insights-icon.svg';
import activeGetfeedbackIcon from '../../static/img/activeGetFeedback.svg';
import activesettingIcon from '../../static/img/activesetting.svg';
import activetaskIcon from '../../static/img/activetask.svg';
import ExitHub from '../../static/img/exithubgray26.svg';
import getfeedbackIcon from '../../static/img/getfeedback32gray.svg';
import classIcon from '../../static/img/insights.svg';
import jeddaiIcon from '../../static/img/jeddaigray32.svg';
import settingIcon from '../../static/img/setting.svg';
import { useAllDocuments, useProfile } from '../state/hooks';
import {
  getJeddaiRole,
  canAccessInsights,
  canAccessJeddAI,
  canAccessSchoolWorkForStudent,
  canViewJeddAI,
  canAccessMarkingTools,
  canViewCurrentTasks,
  canViewClosedTasks,
  canViewFeedbackRequests,
  canViewSharedResponses,
  canViewFeedbackHistory,
  canAccessAnyTasks,
  getUpgradeOptions,
  canUpgradeTo,
} from '../../utils/userAccessHelper.js';
import { checkIsActive, isShowExitHub } from './rules';
import { t } from '../../utils/lexicon';
import { draftRowId, draftRoutePath } from '../../utils/draftRows';
import { getLastViewedDraftId } from '../../utils/myDraftsPrefs';
import Loader from '../Loader';
import {
  Logo,
  SideBottom,
  SideNavbar,
  SidebarContainer,
  SideBottomContainer,
  SideBottomItem,
} from './style';
import ExclamationIcon from '../../static/img/report-issue.svg';
import ReportIssuePopup from '../ReportIssuePopup';
import lockIcon from '../../static/icons/lock.svg';
import SchoolPlanModal from '../../components2/freemium/SchoolPlanModal';
import { AppContext } from '../../app.context';
import CircularProgressWithLabel from '../../components2/TeacherOnboarding/CircularProgress.jsx';
import { useTour } from '../../TourContext.jsx';
import { isSchoolTeacherRole, isNewUserProfile } from '../../utils/userAccessHelper.js';

// Wrap each queryFn so react-query's QueryFunctionContext doesn't get
// forwarded as the first arg to the service function. Same trap as in
// hooks/usePrefetchDashboard.js — getAssignments(context) → URL becomes
// "/assignments?subjectCode=[object Object]".
const ROUTE_PREFETCH_MAP = {
  '/tasks': [
    { queryKey: ['tasks'], queryFn: () => getTasks() },
    { queryKey: ['assignments'], queryFn: () => getAssignments() },
  ],
  '/classes': [
    { queryKey: ['classes'], queryFn: () => getClasses() },
  ],
  '/tools/commentbanks': [
    { queryKey: ['feedbackBanks'], queryFn: () => getFeedbackBanks() },
  ],
};

const MainSidebar = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: profile, isLoadingdata: isLoadingProfile } = useProfile();
  const { isSidebarOverlayActive } = useContext(AppContext) || {};
  // "Setup Progress" ring card in the sidebar bottom slot — lets school
  // teachers open (or return to) the setup checklist. While the checklist is
  // the visible screen every route item is deselected, so the ring is the
  // only lit element ("you are here").
  const tour = useTour() || {};
  const {
    onboardingProgress = 0,
    showOnboardingComplete = false,
    hasLoadedUserPersistence = false,
    setShowOnboardingComplete,
    requestTourExit,
    tourRelaunchRequested = false,
    // Defaults true so a provider without the flag behaves as before.
    hasSyncedSetupChecklist = true,
  } = tour;
  // Three audiences:
  //  - whenever the checklist is open (covers Help-Centre relaunches by
  //    EXISTING teachers, who have no createdAt stamp), so the navbar always
  //    names the screen the user is on;
  //  - new users with setup left to do, as a persistent re-entry point
  //    (replaces the legacy floating "Incomplete setup" toast);
  //  - an existing teacher for the REST of a Help-Centre relaunch session
  //    (tourRelaunchRequested is session-only): the TourExitWarning copy
  //    promises the Setup Progress button as the way back in, so it must
  //    survive a "Leave setup" for them too. Existing users outside a
  //    relaunch never see it.
  // onboardingProgress is derived from the real checklist tasks, and the
  // ring waits until those tasks are this user's (cache or server), so a
  // finished teacher never sees it come back at 0% (JED-1275).
  const showSetupProgressItem =
    isSchoolTeacherRole(profile) &&
    typeof setShowOnboardingComplete === 'function' &&
    (showOnboardingComplete ||
      ((isNewUserProfile(profile) || tourRelaunchRequested) &&
        hasLoadedUserPersistence &&
        hasSyncedSetupChecklist &&
        onboardingProgress < 100));

  const handlePrefetch = useCallback(
    (link) => {
      const prefetches = ROUTE_PREFETCH_MAP[link];
      if (!prefetches) return;
      prefetches.forEach(({ queryKey, queryFn }) => {
        queryClient.prefetchQuery({ queryKey, queryFn, staleTime: 60_000 });
      });
    },
    [queryClient]
  );

  const jeddaiRole = getJeddaiRole(profile);
  const homePageLink = canViewCurrentTasks(profile)
    ? '/tasks'
    : canViewFeedbackRequests(profile)
    ? '/giveFeedback'
    : '/tasks';
  const upgradeOptions = getUpgradeOptions(profile);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [isSchoolPlanModalOpen, setIsSchoolPlanModalOpen] = useState(false);
  const [lockedFeature, setLockedFeature] = useState('');

  const canViewDocs = !!profile && canAccessJeddAI(profile);
  const {
    data: allDocumentsData,
    isLoadingdata: isLoadingDocumentsData,
    resetData,
    setData,
  } = useAllDocuments(null, canViewDocs);

  const getFirstAccessibleTab = () => {
    if (canViewCurrentTasks(profile)) {
      return '/tasks';
    }
    if (canViewFeedbackRequests(profile)) {
      return '/giveFeedback';
    }
    if (canViewClosedTasks(profile)) {
      return '/completed';
    }
    if (canViewFeedbackHistory(profile)) {
      return '/feedbackHistory';
    }
    if (canViewSharedResponses(profile)) {
      return '/sharedresponses';
    }
    if (canViewJeddAI(profile)) {
      return getFeedbackLink(location, allDocumentsData);
    }
    return '/';
  };
  const createNavItems = () => {
    const jeddaiRole = getJeddaiRole(profile);
    // While the setup checklist is the visible screen, no ROUTE item may read
    // as selected — the URL underneath (usually /tasks) is not what the user
    // is looking at. The Setup Progress item below is the selected one.
    const navCheckIsActive = showOnboardingComplete
      ? () => false
      : checkIsActive;
    const items = [];
    if (canViewJeddAI(profile)) {
      items.push(
        getFeedbackItems(
          jeddaiRole,
          location,
          navCheckIsActive,
          allDocumentsData,
          false
        )
      );
    }

    const shouldShowTasks =
      profile?.permissions?.tasks !== 'HIDDEN' ||
      profile?.permissions?.studentTasks !== 'HIDDEN' ||
      profile?.permissions?.schoolWork !== 'HIDDEN';

    if (shouldShowTasks) {
      items.push(
        taskItems({
          role: jeddaiRole,
          homePageLink,
          location,
          checkIsActive: navCheckIsActive,
          locked: profile?.permissions?.tasks === 'LOCKED',
          canAccessSchoolWork: canAccessSchoolWorkForStudent(profile),
        })
      );
    }

    if (profile?.permissions?.insights !== 'HIDDEN') {
      const isLocked = profile?.permissions?.insights === 'LOCKED';
      items.push(
        classItems({ location, checkIsActive: navCheckIsActive, locked: isLocked })
      );
    }

    if (canAccessMarkingTools(profile) && jeddaiRole !== 'STUDENT') {
      items.push(
        isSettingItems(jeddaiRole, location, navCheckIsActive, false, profile?.country)
      );
    }

    if (isShowExitHub()) {
      items.push(studyGuideItems());
    }

    return items;
  };

  const sideNavItems = createNavItems();

  // Open (or return to) the setup checklist. It renders on /tasks — TourGuard's
  // stay-target — so align the route BEFORE flipping it visible; the guard is
  // not armed yet at push time (no tour/checklist active), so this never warns.
  const handleSetupProgressClick = () => {
    if (history.location.pathname !== '/tasks') {
      history.push('/tasks');
    }
    setShowOnboardingComplete(true);
  };

  const handlePageRoute = (navItem) => {
    if (showOnboardingComplete) {
      // Setup checklist is the visible screen. Study Guides opens a NEW TAB
      // (service.goToStudyGuides) — this tab never leaves onboarding, so it
      // stays live. Every other destination (locked ones included — the
      // upgrade modal portals to body at a z-index below the checklist
      // takeover) must be confirmed via TourExitWarning: TourGuard owns the
      // dialog; confirm tears onboarding down and navigates, cancel stays.
      if (navItem.action) {
        navItem.action();
        return;
      }
      if (typeof requestTourExit === 'function') {
        requestTourExit(navItem.link);
        return;
      }
    }

    if (navItem.locked) {
      setLockedFeature(navItem.name || '');
      setIsSchoolPlanModalOpen(true);
      return;
    }

    if (navItem.action) {
      navItem.action();
      return;
    }

    const currentPath = history.location.pathname;
    if (currentPath !== navItem.link) {
      history.push(navItem.link);
    }
  };

  useEffect(() => {
    if (!profile) return;

    const currentPath = location.pathname;
    const shouldRedirect =
      (currentPath.startsWith('/tasks') && !canViewCurrentTasks(profile)) ||
      (currentPath.startsWith('/completed') && !canViewClosedTasks(profile)) ||
      (currentPath.startsWith('/giveFeedback') &&
        !canViewFeedbackRequests(profile)) ||
      (currentPath.startsWith('/feedbackHistory') &&
        !canViewFeedbackHistory(profile)) ||
      (currentPath.startsWith('/sharedresponses') &&
        !canViewSharedResponses(profile)) ||
      (currentPath.startsWith('/classes-insights') && !canAccessInsights(profile)) ||
      (currentPath.startsWith('/tools/markingcriterias') &&
        !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/tools/commentbanks') &&
        !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/tools/successcriterias') &&
        !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/markingTemplates') &&
        !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/jeddai') && !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/advanced-marking') &&
        !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/tools/advanced-controls') &&
        !canAccessMarkingTools(profile)) ||
      (currentPath.startsWith('/getFeedback') && !canViewJeddAI(profile)) ||
      (currentPath.startsWith('/documents/') &&
        !canViewJeddAI(profile) &&
        !currentPath.startsWith('/feedbackRequest/documentsReview') &&
        !currentPath.match(/\/feedbackHistory\/documentsReview/i));

    if (shouldRedirect) {
      const firstAccessibleTab = getFirstAccessibleTab();
      history.push(firstAccessibleTab);
    }
  }, [profile, location.pathname]);

  // TanStack Query v4 reports isLoading === true for a query disabled via
  // `enabled: false` whose cache is still empty — useAllDocuments is gated on
  // canViewDocs, so non-JeddAI users would have hit the loader forever and
  // never seen the sidebar. Only block on documents loading if we actually
  // intended to fetch them.
  if (isLoadingProfile || (canViewDocs && isLoadingDocumentsData)) {
    return <Loader />;
  }

  return (
    <SidebarContainer
      isBlurred={isSidebarOverlayActive}
      isSetupCompleteOpen={showOnboardingComplete}
    >
      <SideNavbar>
        <a
          href="/"
          onClick={(e) => {
            // The logo is part of the navbar: while the setup checklist is
            // open, leaving via it needs the same confirmation as any other
            // item (and the default hard page load would bypass TourGuard).
            if (showOnboardingComplete && typeof requestTourExit === 'function') {
              e.preventDefault();
              requestTourExit('/');
            }
          }}
        >
          <Logo src="./img/logo.svg" />
        </a>
        <ul>
          {sideNavItems.map((navItem) => {
            const active = navItem.isActive;
            const showActiveBackground =
              active && navItem.needsActiveBackground;
            const iconSize = navItem.iconSize || 48;
            return (
              <React.Fragment key={navItem.link}>
                {navItem.hasSeparator && (
                  <hr style={{ width: '100%', margin: '5px 0' }} />
                )}
                <li
                  className={`${navItem.locked ? 'locked' : ''} ${
                    navItem.isStudyGuide ? 'study-guide' : ''
                  }${showActiveBackground ? ' active-bg' : ''}`}
                  onClick={() => handlePageRoute(navItem)}
                  onMouseEnter={() => handlePrefetch(navItem.link)}
                >
                  <div
                    className={`icon-wrapper${
                      showActiveBackground ? ' active-bg' : ''
                    }`}
                  >
                    <img
                      src={active ? navItem.activeIcon : navItem.icon}
                      style={{
                        cursor: active ? 'auto' : 'pointer',
                        width: iconSize,
                        height: iconSize,
                      }}
                    />
                    {navItem.locked && (
                      <img className="lock-badge" src={lockIcon} />
                    )}
                  </div>
                  <span className={active ? 'active' : ''}>{navItem.name}</span>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </SideNavbar>
      {isShowExitHub() && (
        <>
          <SideBottomContainer>
            {showSetupProgressItem && (
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <CircularProgressWithLabel
                  value={onboardingProgress}
                  onClick={handleSetupProgressClick}
                />
              </div>
            )}
            <SideBottomItem onClick={() => setShowReportPopup(true)}>
              <img src={ExclamationIcon} />
              <p>Report Issue</p>
            </SideBottomItem>
          </SideBottomContainer>
          {showReportPopup && (
            <ReportIssuePopup onClose={() => setShowReportPopup(false)} />
          )}
          <SchoolPlanModal
            isOpen={isSchoolPlanModalOpen}
            onClose={() => setIsSchoolPlanModalOpen(false)}
            feature={lockedFeature}
          />
        </>
      )}
    </SidebarContainer>
  );
};

export default MainSidebar;

function taskItems({
  role,
  homePageLink,
  location,
  checkIsActive,
  locked,
  canAccessSchoolWork,
}) {
  const paths = [
    '/',
    '/tasks',
    '/completed',
    '/submissions/',
    '/giveFeedback',
    '/feedbackHistory',
    '/sharedresponses',
    '/feedbackRequest/documentsReview',
    '/feedbackHistory/documentsReview',
    '/submissions',
    '/sharedtasks',
  ];

  const isStudent = role === 'STUDENT';
  const isLocked = locked || (isStudent && !canAccessSchoolWork);

  return {
    icon: taskIcon,
    activeIcon: activetaskIcon,
    isActive: checkIsActive(location, paths),
    name: `${role === 'STUDENT' ? 'School Work' : 'Tasks'}`,
    link: homePageLink,
    locked: isLocked,
  };
}

function classItems({ location, checkIsActive, locked }) {
  const paths = ['/classes-insights', '/class-insights', '/classes'];

  return {
    icon: classIcon,
    activeIcon: activeClassIcon,
    isActive: checkIsActive(location, paths),
    name: 'Class Insights',
    link: '/classes-insights',
    locked,
  };
}

const getFeedbackLink = (location, allDocumentsData) => {
  if (!isLoggedIn()) {
    return location.pathname;
  }
  // A row can name nothing openable, and an assignment-backed draft opens the
  // wizard rather than a document — so the route comes from the row's shape, not
  // from whichever id happens to be populated.
  const openable = (
    Array.isArray(allDocumentsData) ? allDocumentsData : []
  ).filter(draftRoutePath);
  if (openable.length === 0) {
    return '/getFeedback';
  }
  const lastDraftId = getLastViewedDraftId();
  const targetDraft =
    (lastDraftId &&
      openable.find((doc) => String(draftRowId(doc)) === lastDraftId)) ||
    openable[0];
  return draftRoutePath(targetDraft);
};
function get404Items() {
  return [];
}

function getFeedbackItems(
  role,
  location,
  checkIsActive,
  allDocumentsData,
  locked = false
) {
  const paths = ['/getFeedback', '/documents', '/drafts', '/view/submissions'];

  return {
    icon: `${role === 'STUDENT' ? getfeedbackIcon : jeddaiIcon}`,
    activeIcon: `${
      role === 'STUDENT' ? activeGetfeedbackIcon : jeddaiIconIcon
    }`,
    isActive: checkIsActive(location, paths),
    name: 'My Drafts',
    link: getFeedbackLink(location, allDocumentsData),
    locked,
  };
}

function isSettingItems(role, location, checkIsActive, locked = false, country) {
  const paths = [
    '/tools/markingcriterias',
    '/tools/rubrics',
    '/tools/commentbanks',
    '/tools/successcriterias',
    '/tools/shortanswers',
    '/tools/coursematerials',
    '/jeddai',
    '/sharedmarkingtools',
    '/advanced-marking',
    '/tools/advanced-controls',
  ];

  return {
    icon: settingIcon,
    activeIcon: activesettingIcon,
    name: t('Marking Tools', country),
    isActive: checkIsActive(location, paths),
    link: '/tools/rubrics',
    locked,
  };
}

function studyGuideItems() {
  return {
    icon: ExitHub,
    activeIcon: ExitHub,
    name: 'Study Guides',
    isActive: false,
    link: 'study-guides',
    action: goToStudyGuides,
    hasSeparator: true,
    isStudyGuide: true,
  };
}
